// src/components/MiscExpenseManager/MiscExpenseManager.jsx
// or wherever you keep it.

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMiscExpenses,
  createMiscExpense,
  updateMiscExpense,
  deleteMiscExpense,
} from "../../../store/miscExpenseDataSlice";
import "../../../Styles/Common.css"; // For consistent styling
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const MiscExpenseManager = () => {
  const dispatch = useDispatch();
  const { data: miscExpenses, loading, error } = useSelector((state) => state.miscExpenseData);

  // Searching & pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Show 10 items per page

  // For the form
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    id: "",
    tc_id: "",
    meTitle: "",
    meBillFor: "",
    meCost: "",
    meBill: null, // if uploading a file
    meDesc: "",
    meDate: "",
    status: "A",
  });

  // Load data on mount
  useEffect(() => {
    dispatch(fetchMiscExpenses());
  }, [dispatch]);

  // Show any errors as a toast
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Handler for adding new record
  const handleAdd = () => {
    setEditMode(false);
    setIsFormOpen(true);
    // reset form
    setFormData({
      id: "",
      tc_id: "",
      meTitle: "",
      meBillFor: "",
      meCost: "",
      meBill: null, // if uploading a file
      meDesc: "",
      meDate: "",
      status: "A",
    });
  };
  // Handler for editing existing record
  const handleEdit = (record) => {
    setEditMode(true);
    setIsFormOpen(true);
    setFormData({
      id: record.id,
      tc_id: record.tc_id || "",
      meTitle: record.meTitle || "",
      meBillFor: record.meBillFor || "",
      meCost: record.meCost || "",
      meBill: null, // user can override by uploading a new file
      meDesc: record.meDesc || "",
      meDate: record.meDate || "",
      status: record.status || "A",
    });
  };

  const closeForm = () => setIsFormOpen(false);

  // Form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // File input for meBill
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData((prev) => ({ ...prev, meBillFile: e.target.files[0] }));
    }
  };

  // Submit form for create/update
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        const payload = { id: formData.id, formValues: formData };
        await dispatch(updateMiscExpense(payload)).unwrap();
        toast.success("Record updated successfully!");
      } else {
        await dispatch(createMiscExpense(formData)).unwrap();
        toast.success("Record created successfully!");
      }
      setIsFormOpen(false);
    } catch (err) {
      toast.error("Operation failed.");
    }
  };

  // Delete record
  const handleDelete = async (record) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        await dispatch(deleteMiscExpense(record.id)).unwrap();
        toast.success("Record deleted successfully!");
      } catch (err) {
        toast.error("Failed to delete record.");
      }
    }
  };

  // Searching
  // For example, filter by meTitle. Adjust if you'd prefer meDesc or something else.
  const filteredData = miscExpenses.filter((item) =>
    item.meTitle?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => setCurrentPage(page);

  return (
    <div className="container">
      <div className="header-container">
        <h2>Misc Expense Manager</h2>
        <button className="add-lead-btn" onClick={handleAdd}>
          <i className="fa fa-plus"></i>
        </button>
      </div>

      {/* Searching  */}
      <div className="feedback-filters">
        <input
          type="text"
          placeholder="Search by Title..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="filter-input"
        />
      </div>

      {/* combined loading */}
      {loading && <p>Loading data...</p>}

      {/* error shown via toast, but you can also show it here */}
      {error && <p className="error">{error}</p>}

      {/* Table display */}
      <table className="table">
        <thead>
          <tr>
            <th>#</th>
            <th>TC_ID</th>
            <th>Title</th>
            <th>Billed For/To</th>
            <th>Cost</th>
            <th>File</th>
            <th>Description</th>
            <th>Date</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map((rec, index) => (
            <tr key={rec.id}>
              <td>{indexOfFirstItem + index + 1}</td>
              <td>{rec.tc_id}</td>
              <td>{rec.meTitle}</td>
              <td>{rec.meBillFor}</td>
              <td>{rec.meCost}</td>
              <td>
                {rec.meBill ? (
                  <a href={`https://iteyonikservicesinstitute.com/${rec.meBill}`} target="_blank" rel="noreferrer">
                    Download
                  </a>
                ) : (
                  "N/A"
                )}
              </td>
              <td>{rec.meDesc}</td>
              <td>{rec.meDate}</td>
              <td>{rec.status}</td>
              <td>
                <button
                  className="icon-btn update-btn"
                  onClick={() => handleEdit(rec)}
                >
                  <i className="fa fa-edit" aria-hidden="true"></i>
                </button>
                <button
                  className="icon-btn delete-btn"
                  onClick={() => handleDelete(rec)}
                  style={{ marginLeft: "0.5rem" }}
                >
                  <i className="fa fa-trash" aria-hidden="true"></i>
                </button>
              </td>
            </tr>
          ))}
          {currentItems.length === 0 && (
            <tr>
              <td colSpan={8} style={{ textAlign: "center" }}>
                No records found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              className={currentPage === i + 1 ? "active" : ""}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* Form Popup */}
      {isFormOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>{editMode ? "Edit Misc Expense" : "Add Misc Expense"}</h3>
            <form onSubmit={handleSubmit} className="form-grid">
              <div className="form-group">
                <label>TC_ID:</label>
                <input
                  type="number"
                  name="tc_id"
                  value={formData.tc_id}
                  onChange={handleChange}
                  required
                />
                <label>Title:</label>
                <input
                  type="text"
                  name="meTitle"
                  value={formData.meTitle}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Billed To (Name):</label>
                <input
                  type="text"
                  name="meBillFor"
                  value={formData.meBillFor}
                  onChange={handleChange}
                  required
                />
                <label>Cost:</label>
                <input
                  type="number"
                  step="0.01"
                  name="meCost"
                  value={formData.meCost}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Description:</label>
                <input
                  type="text"
                  name="meDesc"
                  value={formData.meDesc}
                  onChange={handleChange}
                />
                <label>Date:</label>
                <input
                  type="datetime-local"
                  name="meDate"
                  value={formData.meDate}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Status:</label>
                <select name="status" value={formData.status} onChange={handleChange}>
                  <option value="A">Active</option>
                  <option value="D">Deactivated</option>
                </select>
              </div>
              {/* File upload */}
              <div className="form-group">
                <label>Bill (optional):</label>
                <input type="file" onChange={handleFileChange} />
                <div className="form-actions">
                  <button type="submit" className="fa fa-check submit-icon"></button>
                  <button
                    type="button"
                    className="fa fa-times cancel-icon"
                    onClick={() => setIsFormOpen(false)}
                  ></button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

export default MiscExpenseManager;
